import { z } from 'zod';
import { LogoutReason } from '../../../shared/auth';
import {
  INACTIVITY_CHANNEL_NAME,
  INACTIVITY_TAB_ID_STORAGE_KEY,
} from './constants';

export const SharedActivityPayloadSchema = z.object({
  type: z.literal('activity'),
  sessionKey: z.string().min(1),
  userId: z.string().min(1),
  inactivityLimitMs: z.number().finite().positive(),
  timestamp: z.number().finite(),
  sourceTabId: z.string().min(1),
});

export const SharedLogoutPayloadSchema = z.object({
  type: z.literal('forceLogout'),
  sessionKey: z.string().min(1),
  userId: z.string().min(1),
  timestamp: z.number().finite(),
  reason: z.nativeEnum(LogoutReason),
  sourceTabId: z.string().min(1),
});

export const SharedInactivityPayloadSchema = z.discriminatedUnion('type', [
  SharedActivityPayloadSchema,
  SharedLogoutPayloadSchema,
]);

export type SharedActivityPayload = z.infer<typeof SharedActivityPayloadSchema>;
export type SharedLogoutPayload = z.infer<typeof SharedLogoutPayloadSchema>;
export type SharedInactivityPayload = z.infer<
  typeof SharedInactivityPayloadSchema
>;

let cachedTabId: string | null = null;

export function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tab-${crypto.randomUUID()}`;
  }

  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateTabId(): string {
  if (cachedTabId) {
    return cachedTabId;
  }

  if (typeof window !== 'undefined') {
    try {
      const storedTabId = window.sessionStorage.getItem(
        INACTIVITY_TAB_ID_STORAGE_KEY
      );
      if (storedTabId) {
        cachedTabId = storedTabId;
        return cachedTabId;
      }
    } catch {
      // Ignore storage access failures.
    }
  }

  cachedTabId = createTabId();

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(INACTIVITY_TAB_ID_STORAGE_KEY, cachedTabId);
    } catch {
      // Ignore storage access failures.
    }
  }

  return cachedTabId;
}

export function openInactivityChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }

  return new BroadcastChannel(INACTIVITY_CHANNEL_NAME);
}

export function getLastActivityStorageKey(sessionKey: string): string {
  return `periverse.auth.inactivity.lastActivity.${sessionKey}`;
}

export function getForceLogoutStorageKey(sessionKey: string): string {
  return `periverse.auth.inactivity.forceLogout.${sessionKey}`;
}

export function readStoredLastActivity(
  sessionKey: string
): SharedActivityPayload | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(getLastActivityStorageKey(sessionKey));
  if (!raw) {
    return null;
  }

  try {
    return SharedActivityPayloadSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function readStoredForceLogout(
  sessionKey: string
): SharedLogoutPayload | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(getForceLogoutStorageKey(sessionKey));
  if (!raw) {
    return null;
  }

  try {
    return SharedLogoutPayloadSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function publishSharedActivity(
  sessionKey: string,
  payload: SharedActivityPayload,
  channel: BroadcastChannel | null
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getLastActivityStorageKey(sessionKey),
    JSON.stringify(payload)
  );
  channel?.postMessage(payload);
}

export function publishSharedForceLogout(
  sessionKey: string,
  payload: SharedLogoutPayload,
  channel: BroadcastChannel | null
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    getForceLogoutStorageKey(sessionKey),
    JSON.stringify(payload)
  );
  channel?.postMessage(payload);
}
